import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageProvider';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import GatewayBlocks from '../components/hopphomes/GatewayBlocks';
import HOPPyAIChat from '../components/hopphomes/HOPPyAIChat';
import HowItWorksSection from '../components/hopphomes/HowItWorksSection';
import SocialProofSection from '../components/hopphomes/SocialProofSection';
import VIPBuyerModal from '../components/hopphomes/VIPBuyerModal';
import SecretPropertyCard from '../components/secretdeals/SecretPropertyCard';

const PropertyListings = () => {
  const { dict } = useLanguage();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVIPModal, setShowVIPModal] = useState(false);
  const [filters, setFilters] = useState({
    type: 'elado', // eladó/kiadó
    location: '',
    priceMin: '',
    priceMax: '',
    sizeMin: '',
    sizeMax: '',
    rooms: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const functions = getFunctions();
      const getProperties = httpsCallable(functions, 'getUploadedProperties');
      
      const result = await getProperties();
      if (result.data.success) {
        setProperties(result.data.properties || []);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
      // Mock data for development
      setProperties([
        {
          id: '1',
          title: 'Modern Mobilház - Esztergom',
          price: '43.90 M Ft',
          size: '65 m²',
          rooms: '2+1',
          bathrooms: '1',
          location: 'Esztergom, Szalai út',
          images: ['/galeria1_ai.jpg'],
          features: ['Kulcsrakész', 'Energiatakarékos', 'Modern dizájn'],
          description: 'Prémium mobilház kulcsrakész kivitelben, modern berendezéssel.',
          type: 'elado'
        },
        {
          id: '2',
          title: 'Családi Mobilház - Szentgyörgymező',
          price: '39.90 M Ft',
          size: '37 m²',
          rooms: '2',
          bathrooms: '1',
          location: 'Szentgyörgymező',
          images: ['/galeria2_ai.jpg'],
          features: ['Tetőtér beépítve', 'Kert', 'Parkolóhely'],
          description: 'Hangulatos mobilház csendes környezetben, kerttel.',
          type: 'elado'
        },
        {
          id: '3',
          title: 'Luxus Mobilház - Szentgyörgymező',
          price: '64.90 M Ft',
          size: '65 m²',
          rooms: '3',
          bathrooms: '1',
          location: 'Szentgyörgymező',
          images: ['/galeria3_ai.jpg'],
          features: ['Prémium felszereltség', 'Terasz', 'Okos otthon'],
          description: 'Luxus mobilház minden extrával, okos otthon rendszerrel.',
          type: 'elado'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         property.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = property.type === filters.type;
    const matchesLocation = !filters.location || property.location.toLowerCase().includes(filters.location.toLowerCase());
    
    const price = parseFloat(property.price.replace(/[^\d.]/g, ''));
    const matchesPriceMin = !filters.priceMin || price >= parseFloat(filters.priceMin);
    const matchesPriceMax = !filters.priceMax || price <= parseFloat(filters.priceMax);
    
    const size = parseFloat(property.size.replace(/[^\d]/g, ''));
    const matchesSizeMin = !filters.sizeMin || size >= parseFloat(filters.sizeMin);
    const matchesSizeMax = !filters.sizeMax || size <= parseFloat(filters.sizeMax);
    
    const rooms = parseInt(property.rooms.replace(/[^\d]/g, ''));
    const matchesRooms = !filters.rooms || rooms >= parseInt(filters.rooms);

    return matchesSearch && matchesType && matchesLocation && matchesPriceMin && 
           matchesPriceMax && matchesSizeMin && matchesSizeMax && matchesRooms;
  });

  // Transform property data for SecretPropertyCard format
  const transformPropertyForSecret = (property) => {
    return {
      id: property.id,
      title: property.title,
      type: 'house', // or property.type
      location: property.location,
      price: parseFloat(property.price?.replace(/[^\d.]/g, '')) || 0,
      area_m2: parseInt(property.size) || 0,
      rooms: property.rooms,
      images: property.images || [],
      features: property.features || [],
      description: property.description,
      // Add mock data for demo (in production, these would come from API)
      marketPrice: parseFloat(property.price?.replace(/[^\d.]/g, '')) * 1.1, // 10% higher as market price
      roiPotential: Math.floor(Math.random() * 15) + 5, // Random 5-20% ROI
      daysOnMarket: Math.floor(Math.random() * 30),
      viewCount: Math.floor(Math.random() * 150) + 20,
      isHotDeal: Math.random() > 0.7, // 30% chance of being hot
      hotScore: Math.floor(Math.random() * 40) + 60,
      hotLevel: Math.random() > 0.8 ? 'extreme' : 'high',
      offMarketCertified: Math.random() > 0.8,
      firstLookActive: Math.random() > 0.85,
      sellerData: {
        hasTitle: true,
        hasPhotos: true,
        hasSurvey: Math.random() > 0.5,
        saleTimeline: '3_months',
        avgResponseTime: Math.floor(Math.random() * 24),
        priceReductions: 0
      }
    };
  };

  // Legacy PropertyCard (keeping for fallback)
  const LegacyPropertyCard = ({ property }) => (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
      <div className="relative">
        <img 
          src={property.images[0]} 
          alt={property.title}
          className="w-full h-48 object-cover"
        />
        {/* Development/Test Banner */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500/90 to-yellow-500/90 text-white text-center py-1 px-2">
          <span className="text-xs font-bold animate-pulse">🚧 TESZT HIRDETÉS - FEJLESZTÉS ALATT 🚧</span>
        </div>
        <div className="absolute top-8 left-3">
          <span className="bg-[#e31937] text-white px-2 py-1 rounded-lg text-xs font-bold">
            {property.images.length} kép
          </span>
        </div>
        <div className="absolute top-8 right-3">
          <button className="bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-white font-bold text-xl">{property.price}</h3>
          <span className="text-gray-400 text-sm">{property.price.includes('Ft') ? property.price.split(' ')[1] : ''}</span>
        </div>
        
        <p className="text-gray-300 text-sm mb-3">{property.location}</p>
        
        <div className="flex items-center gap-4 mb-3 text-gray-400 text-sm">
          <span>Alapterület: <span className="text-white font-semibold">{property.size}</span></span>
          <span>Szobák: <span className="text-white font-semibold">{property.rooms}</span></span>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {property.features.slice(0, 3).map((feature, index) => (
            <span key={index} className="bg-[#23272f] text-gray-300 px-2 py-1 rounded-lg text-xs">
              {feature}
            </span>
          ))}
          {property.features.length > 3 && (
            <span className="bg-[#23272f] text-gray-300 px-2 py-1 rounded-lg text-xs">
              +{property.features.length - 3}
            </span>
          )}
        </div>
        
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{property.description}</p>
        
        <div className="flex gap-2">
          <button className="flex-1 bg-[#e31937] hover:bg-[#b91c1c] text-white py-2 px-4 rounded-lg text-sm font-semibold transition-colors">
            Részletek
          </button>
          <button className="bg-[#23272f] hover:bg-[#2a2e37] text-white py-2 px-4 rounded-lg transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Page Header */}
      <div className="border-b border-white/[0.06] px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Eladó Ingatlanok</h1>
            <p className="text-[#9ca3af] text-sm mt-0.5">AI-alapú ingatlan kereső és értékelő</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 bg-[#e31937]/10 border border-[#e31937]/20 rounded-xl px-4 py-2">
            <span className="w-2 h-2 bg-[#e31937] rounded-full animate-pulse" />
            <span className="text-[#e31937] text-xs font-semibold">{properties.length} aktív hirdetés</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search and Filters */}
        <div className="bg-[#111111] border border-white/[0.07] rounded-2xl p-5 mb-6">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Hol keresel? (pl. Esztergom, Szentgyörgymező)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#23272f] border border-white/[0.08] rounded-lg py-3 px-4 pr-12 text-white placeholder-gray-400 focus:outline-none focus:border-[#e31937] focus:ring-1 focus:ring-[#e31937]"
              />
              <button className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#e31937] hover:bg-[#b91c1c] text-white p-2 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Filter Toggles */}
            {/* Desktop Filters */}
            <div className="hidden md:flex flex-col gap-4 mb-6">
              <div className="flex justify-center">
                <div className="flex bg-[#23272f] rounded-lg p-1">
                  <button
                    onClick={() => setFilters({...filters, type: 'elado'})}
                    className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.type === 'elado' 
                        ? 'bg-[#e31937] text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Eladó
                  </button>
                  <button
                    onClick={() => setFilters({...filters, type: 'kiado'})}
                    className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.type === 'kiado' 
                        ? 'bg-[#e31937] text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Kiadó
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-gray-400 text-sm font-medium">Ár (millió Ft):</span>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.priceMin}
                      onChange={(e) => setFilters({...filters, priceMin: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                    <span className="text-gray-400 text-sm">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.priceMax}
                      onChange={(e) => setFilters({...filters, priceMax: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-gray-400 text-sm font-medium">Alapterület (m²):</span>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.sizeMin}
                      onChange={(e) => setFilters({...filters, sizeMin: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                    <span className="text-gray-400 text-sm">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.sizeMax}
                      onChange={(e) => setFilters({...filters, sizeMax: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-gray-400 text-sm font-medium">Szobák száma:</span>
                  <select
                    value={filters.rooms}
                    onChange={(e) => setFilters({...filters, rooms: e.target.value})}
                    className="w-full bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                  >
                    <option value="">Válassz szobaszámot</option>
                    <option value="1">1+ szoba</option>
                    <option value="2">2+ szoba</option>
                    <option value="3">3+ szoba</option>
                    <option value="4">4+ szoba</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Mobile Filters */}
            <div className="flex md:hidden flex-col gap-4 mb-6">
              <div className="flex justify-center">
                <div className="flex bg-[#23272f] rounded-lg p-1 w-full max-w-xs">
                  <button
                    onClick={() => setFilters({...filters, type: 'elado'})}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.type === 'elado' 
                        ? 'bg-[#e31937] text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Eladó
                  </button>
                  <button
                    onClick={() => setFilters({...filters, type: 'kiado'})}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.type === 'kiado' 
                        ? 'bg-[#e31937] text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Kiadó
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <span className="text-gray-400 text-sm font-medium">Ár (millió Ft):</span>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.priceMin}
                      onChange={(e) => setFilters({...filters, priceMin: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                    <span className="text-gray-400 text-sm">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.priceMax}
                      onChange={(e) => setFilters({...filters, priceMax: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-gray-400 text-sm font-medium">Alapterület (m²):</span>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.sizeMin}
                      onChange={(e) => setFilters({...filters, sizeMin: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                    <span className="text-gray-400 text-sm">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.sizeMax}
                      onChange={(e) => setFilters({...filters, sizeMax: e.target.value})}
                      className="flex-1 bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-gray-400 text-sm font-medium">Szobák száma:</span>
                  <select
                    value={filters.rooms}
                    onChange={(e) => setFilters({...filters, rooms: e.target.value})}
                    className="w-full bg-[#23272f] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm focus:border-[#e31937] focus:outline-none transition-colors"
                  >
                    <option value="">Válassz szobaszámot</option>
                    <option value="1">1+ szoba</option>
                    <option value="2">2+ szoba</option>
                    <option value="3">3+ szoba</option>
                    <option value="4">4+ szoba</option>
                  </select>
                </div>
              </div>
            </div>

          {/* Quick Filters */}
          <div className="flex flex-col gap-3">
            <span className="text-gray-400 text-sm">Gyors szűrők:</span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button className="w-full bg-[#23272f] hover:bg-[#2a2e37] text-gray-300 px-4 py-3 rounded-lg text-sm font-medium transition-colors">
                FIX 3% (mt ez?)
              </button>
              <button className="w-full bg-[#23272f] hover:bg-[#2a2e37] text-gray-300 px-4 py-3 rounded-lg text-sm font-medium transition-colors">
                Új építésű
              </button>
              <button className="w-full bg-[#23272f] hover:bg-[#2a2e37] text-gray-300 px-4 py-3 rounded-lg text-sm font-medium transition-colors">
                Részletes keresés
              </button>
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <h2 className="text-lg sm:text-xl font-semibold text-white">
              Eladó esztergomai lakások
            </h2>
            <span className="text-gray-400 text-sm">{filteredProperties.length} találat</span>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-gray-400 text-sm">Rendezés:</span>
            <select className="flex-1 sm:flex-none bg-[#23272f] border border-white/[0.08] rounded px-3 py-2 text-white text-sm">
              <option>Alap rendezés</option>
              <option>Ár szerint növekvő</option>
              <option>Ár szerint csökkenő</option>
              <option>Alapterület szerint</option>
              <option>Legújabb</option>
            </select>
          </div>
        </div>

        {/* Property Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e31937]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:px-0 px-2">
            {filteredProperties.map((property) => (
              <SecretPropertyCard 
                key={property.id} 
                property={transformPropertyForSecret(property)}
                showMatchScore={true}
                showRisk={true}
                expanded={false}
              />
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && filteredProperties.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🏠</div>
            <h3 className="text-xl font-semibold text-white mb-2">Nincs találat</h3>
            <p className="text-gray-400 mb-6">Próbálj meg más keresési feltételeket.</p>
            <button 
              onClick={() => {
                setFilters({
                  type: 'elado',
                  location: '',
                  priceMin: '',
                  priceMax: '',
                  sizeMin: '',
                  sizeMax: '',
                  rooms: ''
                });
                setSearchQuery('');
              }}
              className="bg-[#e31937] hover:bg-[#b91c1c] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Szűrők törlése
            </button>
          </div>
        )}

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-500/40 rounded-xl p-6 mt-12">
          <div className="flex items-start gap-4">
            <div className="text-3xl">🚧</div>
            <div>
              <h3 className="text-white font-bold text-lg mb-2">Fejlesztési Verzió - Teszt Környezet</h3>
              <p className="text-gray-300 text-sm mb-4">
                Ez egy fejlesztési verzió. Az itt látható hirdetések teszt célúak és nem valós eladó ingatlanokat reprezentálnak. 
                A végleges verzióban valós, AI-alapú elemzésen átesett ingatlanok fognak megjelenni.
              </p>
              <div className="flex gap-3 flex-wrap">
                <span className="bg-orange-500/30 text-orange-200 px-3 py-1 rounded-lg text-xs">🧪 Teszt Adatok</span>
                <span className="bg-orange-500/30 text-orange-200 px-3 py-1 rounded-lg text-xs">🚧 Fejlesztés Alatt</span>
                <span className="bg-orange-500/30 text-orange-200 px-3 py-1 rounded-lg text-xs">🤖 AI Funkciók Tesztelése</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Existing Property Listings */}
      <div className="bg-gradient-to-b from-[#0a0a0a] to-black py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              🏠 Eladó Ingatlanok
            </h2>
            <p className="text-gray-300">
              Böngéssz a feltöltött ingatlanok között
            </p>
          </div>
        </div>
      </div>

      {/* VIP Modal */}
      <VIPBuyerModal
        isOpen={showVIPModal}
        onClose={() => setShowVIPModal(false)}
        onSubscribe={(plan) => {
          console.log('VIP subscription:', plan);
          setShowVIPModal(false);
          alert(`VIP előfizetés aktiválva: ${plan.title}`);
        }}
      />
    </div>
  );
};

export default PropertyListings;
